import classNames from 'classnames'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ITabButtonHeaderTemplate } from 'core-components/library/tabButtons/models/ITabButtons'
import { Tabs } from 'core-components/library/tabButtons/tabButton/Tabs'

import { IScrollableTabButtons } from '../models/IScrollableTabButtons'
import { TabButtons } from '../tabButton/TabButtons'

import { ArrowButton } from './ArrowButton'

import styles from './ScrollableTabButtons.module.scss'

export const ScrollableTabButtons = (props: IScrollableTabButtons) => {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [scrollButtonVisible, setScrollButtonVisible] = useState({ left: false, right: false })
  const scrollableClasses = useMemo(() => {
    return {
      root: props.classes?.root,
      header: classNames(styles.tabHeader, props.classes?.header || ''),
      button: classNames(styles.tabButton, props.classes?.button || ''),
    }
  }, [props.classes])

  const arrowButtonClick = (direction: 'left' | 'right') => {
    const root = scrollRef.current
    if (root) {
      const scrollCoefficient = props.scrollCoefficients || 2
      const scrollStep = root.clientWidth / scrollCoefficient
      const delta = direction === 'left' ? -1 : 1
      root.scrollLeft += scrollStep * delta
    }
  }

  const recalculateButtonVisible = useCallback(() => {
    const root = scrollRef.current

    if (root) {
      const roundValue = 2
      const rightVisible = root.scrollWidth - (root.clientWidth + root.scrollLeft) > roundValue
      const newButtonVisible = {
        left: root.scrollLeft !== 0,
        right: rightVisible,
      }
      setScrollButtonVisible(newButtonVisible)
    }
  }, [])

  useEffect(() => {
    recalculateButtonVisible()

    window.addEventListener('resize', recalculateButtonVisible)
    return () => window.removeEventListener('resize', recalculateButtonVisible)
  }, [recalculateButtonVisible, props.items])

  const renderHeader = ({
    activeTab,
    onChangeTab,
    tabsValidators,
    tabLoaderVisible,
  }: ITabButtonHeaderTemplate) => {
    return (
      <div
        className={styles.header}
        ref={scrollRef}
        onScroll={recalculateButtonVisible}
        data-testid={'scrollable-tabButtons-header'}
      >
        {scrollButtonVisible.left ? (
          <ArrowButton
            className={classNames(styles.leftArrow, props.arrowButtonClassName)}
            direction={'left'}
            onClick={() => arrowButtonClick('left')}
          />
        ) : null}

        <Tabs
          items={props.items}
          activeTab={activeTab}
          onChangeTab={onChangeTab}
          tabLoaderVisible={tabLoaderVisible}
          tabsValidators={tabsValidators}
          titleRender={props.titleRender}
          classes={scrollableClasses}
          contentAlwaysExisting={props.contentAlwaysExisting}
          headerContent={props.headerContent}
        />

        {scrollButtonVisible.right ? (
          <ArrowButton
            className={classNames(styles.rightArrow, props.arrowButtonClassName)}
            direction={'right'}
            onClick={() => arrowButtonClick('right')}
          />
        ) : null}
      </div>
    )
  }

  return (
    <div className={styles.root}>
      <TabButtons {...props} classes={scrollableClasses} headerTemplate={renderHeader} />
    </div>
  )
}
